import { useEffect, type RefObject } from 'react';

// Tracks how many instances of this hook currently have `open` true, across
// the whole app — lets an ancestor (e.g. a Dialog's onEscapeKeyDown) check
// "is some nested popover open right now" and preventDefault its own
// Escape-to-close, so dismissing an inner popover doesn't also close it.
let openPopoverCount = 0;

/** True while at least one popover/palette/menu using this hook is open —
 *  check this from a parent Dialog's `onEscapeKeyDown` and call
 *  `event.preventDefault()` when true, so Escape closes the nested popover
 *  only, not the dialog underneath it (both listen on `document`, and the
 *  dialog's own Escape handling has no way to know about a hand-rolled
 *  popover otherwise).
 *
 *  App-wide, not per-dialog: fine while every consumer's parent dialog is a
 *  de-facto singleton (today, only one AddScriptModal is ever mounted at a
 *  time). If a future dialog can have multiple concurrent instances, this
 *  needs scoping (React context per dialog, or DOM-containment against that
 *  dialog's own content ref) — a popover open in one instance would
 *  otherwise suppress Escape in an unrelated one. */
export function isDismissablePopoverOpen(): boolean {
  return openPopoverCount > 0;
}

/** Calls `onDismiss` on any pointer-down outside `containerRef`, or on Escape,
 *  while `open` — the shared dismiss behaviour for popovers, palettes, and
 *  menus, so the pattern (including the keyboard-only path) lives in one
 *  place. */
export function useDismissOnOutsidePointer(
  open: boolean,
  containerRef: RefObject<HTMLElement | null>,
  onDismiss: () => void,
): void {
  useEffect(() => {
    if (!open) return;
    openPopoverCount++;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && containerRef.current?.contains(target)) return;
      onDismiss();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      openPopoverCount--;
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, containerRef, onDismiss]);
}
